/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import MarkdownText from 'components/common/MarkdownText';
import Spell from 'model/stats/Spell';

interface SpellPopupProps {
  spell: Spell;
}

const componentString = (s: Spell): string => {
  const parts: string[] = [];
  if (s.verbal) parts.push('V');
  if (s.somatic) parts.push('S');
  if (s.material) parts.push(s.material_specified ? `M (${s.material_specified})` : 'M');
  return parts.join(', ');
};

const levelOrdinal = (level: number): string => {
  if (level === 0) return 'Cantrip';
  const lastTwo = level % 100;
  const last = level % 10;
  if (lastTwo >= 11 && lastTwo <= 13) return `${level}th-level`;
  if (last === 1) return `${level}st-level`;
  if (last === 2) return `${level}nd-level`;
  if (last === 3) return `${level}rd-level`;
  return `${level}th-level`;
};

const SpellPopup: React.FC<SpellPopupProps> = ({ spell }) => {
  const [isInSvg, setIsInSvg] = useState(false);
  const [isInPopup, setIsInPopup] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [checkVisiblity, setCheckVisiblity] = useState(0);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const svgRef = useRef<HTMLSpanElement>(null); // Reference for the <svg> element

  const handleMouseEnter = (fromSvg: boolean) => {
    adjustPopupCount(fromSvg, true);
  }

  const handleMouseLeave = (fromSvg: boolean) => {
    adjustPopupCount(fromSvg, false);
  };

  useEffect(() => {
    const newVisible = isInPopup || isInSvg;
    if (newVisible !== isPopupVisible) {
      console.log('setting visibilty: ', newVisible);
      setIsPopupVisible(newVisible);
    }
  }, [checkVisiblity]);

  const adjustPopupCount = (fromSvg: boolean, isEnter: boolean) => {
    const newShow = isEnter || (isInPopup && isInSvg);
    // Set a delay before showing or hiding the popup
    if (isPopupVisible !== newShow) {
      if (hoverTimeout.current) {
        // Prevent multiple timers
        console.log('timer stopped');
        clearTimeout(hoverTimeout.current);
      }
      console.log('timer started');
      hoverTimeout.current = setTimeout(() => {
        console.log('timer completed');
        setCheckVisiblity(checkVisiblity+1);
        hoverTimeout.current = null;
      }, isEnter ? 400 : (fromSvg ? 200 : 0));
    }
    if (fromSvg) {
      console.log('isInSvg: ', isEnter);
      setIsInSvg(isEnter);
    } else {
      console.log('isInPopup: ', isEnter);
      setIsInPopup(isEnter);
    }
  };

  useEffect(() => {
    if (isPopupVisible && svgRef.current && popupRef.current) {
      const svgRect = svgRef.current.getBoundingClientRect();
      const popupRect = popupRef.current.getBoundingClientRect();
  
      const spaceBelow = window.innerHeight - svgRect.bottom;
      const spaceAbove = svgRect.top;
  
      const showAbove = (spaceBelow < spaceAbove) && (spaceBelow < popupRect.height);
  
      setPopupPosition({
        x: svgRect.left,
        y: showAbove ? svgRect.top - popupRect.height - 5 : svgRect.bottom - 5,
      });
    }
  }, [isPopupVisible]);
  
  //console.log(`Spell ${spell.name}, popupCount: ${popupCount}, isPopupVisible: ${isPopupVisible}, hoverTimeout.current: ${hoverTimeout?.current ?? 'null'}`)
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span ref={svgRef} onMouseEnter={() => handleMouseEnter(true)} onMouseLeave={() => handleMouseLeave(true)}>
        <svg width="10" height="10" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="black" strokeWidth="2" fill="none" />
          <text x="50%" y="55%" fontSize="16" textAnchor="middle" fontStyle="italic" fill="black" dy=".3em">i</text>
        </svg>&nbsp;
      </span>
      {spell.name}
      {spell.ritual && <span className="ritual">&nbsp;(ᚱ)</span>}

      {isPopupVisible && (
        <div onMouseEnter={() => handleMouseEnter(false)} onMouseLeave={() => handleMouseLeave(false)}
          className="popup"
          ref={popupRef}
          style={{
            position: 'fixed',
            left: `${popupPosition.x - 10}px`, // Use initial mouse coordinates
            top: `${popupPosition.y - 5}px`, // Offset the popup slightly below the mouse
            border: '1px solid black',
            padding: '10px',
            backgroundColor: 'white',
            zIndex: 700,
            width: '300px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div className="spell-header">
            <span className="spell-name">{spell.name}</span>
            <span className="spell-info">
              {spell.level > 0 && <>{levelOrdinal(spell.level)} &nbsp;</>}
              {spell.school.name}
              {spell.level === 0 && <> &nbsp;{levelOrdinal(spell.level)}</>}
              <span className="ritual">
                {spell.ritual && ' (ᚱitual)'}
              </span>
            </span>
          </div>
          <strong>Casting Time:</strong> {spell.casting_time}
          <br />
          <strong>Range:</strong> {spell.range_text}
          <br />
          <strong>Components:</strong> {componentString(spell)}
          <br />
          <strong>Duration:</strong> {spell.concentration && 'Concentration,'}
          {` ${spell.duration}`}
          <br />
          <MarkdownText text={spell.desc} mon={null} />
          {spell.higher_level && (
            <>
              <br />
              <strong>At Higher Levels.</strong>
              <MarkdownText text={spell.higher_level} mon={null} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SpellPopup;
